/**
 * ELANTRA — Semantic AI Search
 * Runs a local scent-aware semantic search in parallel with Shopify's
 * predictive search and injects "AI Suggestions" above the native results.
 *
 * Architecture:
 *  1. Delegate-listens on document for input on any search box
 *  2. Debounce 420ms (after native Shopify's 200ms fires first)
 *  3. Score catalog with expanded scent terms
 *  4. Inject #el-ai-suggestions before #predictive-search-products
 *  5. MutationObserver re-injects after each morph() call
 *
 * Optional backend: if the <script> tag has data-proxy-url set, results
 * are also fetched from the OpenAI proxy (same endpoint as AI assistant).
 */
(function () {
  'use strict';

  /* ── Scent semantic map ─────────────────────────────────── */
  const SCENT_MAP = {
    dark:      ['oud', 'dark', 'noir', 'smoke', 'intense', 'night', 'black', 'shadow'],
    fresh:     ['fresh', 'aqua', 'sport', 'light', 'clean', 'citrus', 'green', 'crisp'],
    floral:    ['floral', 'rose', 'jasmine', 'women', 'feminine', 'her', 'peony', 'lily', 'bloom'],
    woody:     ['wood', 'cedar', 'sandalwood', 'oud', 'amber', 'bark', 'vetiver'],
    oriental:  ['oriental', 'spicy', 'warm', 'vanilla', 'amber', 'musk', 'patchouli', 'incense'],
    citrus:    ['citrus', 'lemon', 'orange', 'bergamot', 'fresh', 'lime', 'mandarin', 'grapefruit'],
    gourmand:  ['sweet', 'vanilla', 'caramel', 'sugar', 'honey', 'gourmand', 'praline'],
    aquatic:   ['aqua', 'ocean', 'sea', 'water', 'marine', 'fresh', 'ozone'],
    leather:   ['leather', 'smoke', 'tobacco', 'dark', 'intense', 'animalic'],
    gym:       ['sport', 'fresh', 'aqua', 'light', 'active', 'clean', 'energy'],
    date:      ['seductive', 'dark', 'intense', 'night', 'oud', 'sensual', 'romantic', 'amber'],
    office:    ['light', 'fresh', 'clean', 'professional', 'subtle', 'soft', 'citrus'],
    summer:    ['fresh', 'citrus', 'aqua', 'light', 'floral', 'coconut', 'tropical'],
    winter:    ['oud', 'warm', 'amber', 'spicy', 'dark', 'intense', 'incense', 'smoke'],
    arabic:    ['oud', 'amber', 'oriental', 'intense', 'musk', 'rose', 'incense'],
    men:       ['woody', 'dark', 'fresh', 'sport', 'aqua', 'aromatic', 'leather'],
    women:     ['floral', 'feminine', 'rose', 'sweet', 'light', 'jasmine', 'delicate'],
    unisex:    ['musk', 'woody', 'fresh', 'amber', 'citrus', 'clean'],
    cheap:     ['affordable', 'value', 'budget'],
    luxury:    ['premium', 'exclusive', 'rare', 'niche', 'extrait'],
    strong:    ['intense', 'heavy', 'projection', 'sillage', 'long-lasting', 'edp'],
    subtle:    ['light', 'fresh', 'soft', 'edt', 'delicate', 'clean'],
    night:     ['dark', 'intense', 'seductive', 'oud', 'noir', 'smoke'],
    day:       ['fresh', 'light', 'clean', 'citrus', 'floral', 'casual'],
  };

  /* ── Catalog caching ─────────────────────────────────────── */
  let _catalog = null;
  let _catalogTime = 0;
  const CACHE_TTL = 5 * 60 * 1000;

  async function getCatalog() {
    if (_catalog && Date.now() - _catalogTime < CACHE_TTL) return _catalog;
    try {
      const res = await fetch('/collections/all/products.json?limit=250');
      const data = await res.json();
      _catalog = data.products || [];
      _catalogTime = Date.now();
      return _catalog;
    } catch {
      return [];
    }
  }

  /* ── Semantic scoring ────────────────────────────────────── */
  function expandQuery(query) {
    const keywords = query.toLowerCase().split(/\s+/).filter(w => w.length > 2);
    let expanded = [...keywords];
    Object.entries(SCENT_MAP).forEach(([key, synonyms]) => {
      if (keywords.some(k => key.startsWith(k) || k.startsWith(key))) {
        expanded = [...new Set([...expanded, ...synonyms])];
      }
    });
    return expanded;
  }

  async function semanticScore(query) {
    const catalog = await getCatalog();
    const terms = expandQuery(query);
    if (!terms.length) return [];

    return catalog
      .map(p => {
        const haystack = [
          p.title, p.vendor, p.product_type, p.tags,
          (p.body_html || '').replace(/<[^>]+>/g, ''),
        ].join(' ').toLowerCase();
        const score = terms.reduce((s, t) => s + (haystack.includes(t) ? 1 : 0), 0);
        return { p, score };
      })
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map(x => x.p);
  }

  /* ── Render ──────────────────────────────────────────────── */
  function money(price) {
    const n = parseFloat(price);
    return isNaN(n) ? '' : (n / 100).toFixed(2);
  }

  function buildHTML(products, query) {
    const items = products.map(p => {
      const img = p.images?.[0]?.src;
      const thumb = img ? img.replace(/(\.[a-z]+)(\?.*)?$/i, '_80x80$1') : null;
      const price = p.variants?.[0]?.price;
      return `
        <li class="predictive-search-results__card predictive-search-results__card--product el-ai-card">
          <a href="/products/${p.handle}" class="el-ai-link">
            ${thumb ? `<img src="${thumb}" alt="${p.title}" class="el-ai-img" loading="lazy" width="40" height="40">` : '<div class="el-ai-img el-ai-img--placeholder"></div>'}
            <div class="el-ai-info">
              <span class="el-ai-name">${p.title}</span>
              ${price ? `<span class="el-ai-price">$${money(price)}</span>` : ''}
            </div>
            <svg class="el-ai-arrow" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </a>
        </li>`;
    }).join('');

    return `
      <div id="el-ai-suggestions">
        <div class="el-ai-header">
          <svg class="el-ai-star" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 1L9.545 5.455L14 7L9.545 8.545L8 13L6.455 8.545L2 7L6.455 5.455L8 1Z" fill="currentColor"/>
          </svg>
          <span class="el-ai-label">AI Suggestions</span>
          <span class="el-ai-query">&ldquo;${query}&rdquo;</span>
        </div>
        <ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled el-ai-list">
          ${items}
        </ul>
      </div>`;
  }

  /* ── Injection ───────────────────────────────────────────── */
  function inject(container, html) {
    container.querySelector('#el-ai-suggestions')?.remove();
    const anchor = container.querySelector('#predictive-search-products');
    if (anchor) {
      anchor.insertAdjacentHTML('beforebegin', html);
    } else {
      // fallback: prepend to results inner
      const inner = container.querySelector('[data-search-results]');
      if (inner) inner.insertAdjacentHTML('afterbegin', html);
    }
  }

  /* ── MutationObserver to re-inject after morph ───────────── */
  let _observer = null;

  function watchContainer(container, query) {
    if (_observer) _observer.disconnect();

    _observer = new MutationObserver(() => {
      const hasSuggestions = !!container.querySelector('#el-ai-suggestions');
      const hasProducts = !!container.querySelector('#predictive-search-products');
      if (!hasSuggestions && hasProducts && query === _currentQuery) {
        doSearch(query, container);
      }
    });

    _observer.observe(container, { childList: true, subtree: true });
  }

  /* ── Core search flow ────────────────────────────────────── */
  let _currentQuery = '';

  async function doSearch(query, container) {
    const products = await semanticScore(query);
    if (query !== _currentQuery) return; // stale
    if (!products.length) return;
    inject(container, buildHTML(products, query));
  }

  /* ── Input listener ──────────────────────────────────────── */
  let _timer = null;

  document.addEventListener('input', (e) => {
    const input = /** @type {HTMLInputElement} */ (e.target);
    if (!input.matches('[ref="searchInput"], input[type="search"].search-input')) return;

    const query = input.value.trim();
    _currentQuery = query;

    if (_timer) clearTimeout(_timer);
    document.querySelector('#el-ai-suggestions')?.remove();

    if (query.length < 2) {
      if (_observer) _observer.disconnect();
      return;
    }

    _timer = setTimeout(() => {
      const container =
        input.closest('[ref="predictiveSearchResults"]') ||
        input.closest('.predictive-search-form__content') ||
        document.querySelector('[ref="predictiveSearchResults"]') ||
        document.querySelector('.predictive-search-form__content');
      if (!container) return;

      watchContainer(container, query);
      doSearch(query, container);
    }, 420);
  }, { capture: true });

  // Prefetch catalog when search modal opens
  document.addEventListener('click', (e) => {
    if (e.target.closest('[on\\:click*="toggleDialog"], [href="#search-modal"], [aria-controls="search-modal"]')) {
      getCatalog();
    }
  });
})();
